import styled, { keyframes } from "styled-components";

const rollPulse = keyframes`
  0% {
    transform: translateY(0) scale(1);
    filter: saturate(1);
  }
  50% {
    transform: translateY(-5px) scale(1.025);
    filter: saturate(1.16);
  }
  100% {
    transform: translateY(0) scale(1);
    filter: saturate(1);
  }
`;

const sweep = keyframes`
  0% {
    transform: translateX(-130%) rotate(10deg);
  }
  100% {
    transform: translateX(130%) rotate(10deg);
  }
`;

const overlayIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const modalIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const StyledRandomSlotPicker = styled.div`
  display: inline-flex;
  flex: 0 0 auto;

  .random-slot-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    height: 40px;
    border: 1px solid rgba(89, 244, 119, 0.34);
    border-radius: 999px;
    color: #07130c;
    background: linear-gradient(135deg, #7cff91, #28d765 54%, #17ac4e);
    box-shadow:
      0 12px 26px rgba(22, 184, 87, 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.36);
    padding: 0 16px 0 14px;
    font-family: var(--crypto-display-font);
    font-size: 13px;
    line-height: 1;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    transition:
      filter 0.15s ease,
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }

  .random-slot-trigger:hover,
  .random-slot-trigger:focus-visible {
    filter: brightness(1.04);
    transform: translateY(-1px);
    box-shadow:
      0 16px 30px rgba(22, 184, 87, 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 0.42);
    outline: 0;
  }

  .random-slot-trigger-icon {
    position: relative;
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }

  .random-slot-trigger-icon::before,
  .random-slot-trigger-icon::after {
    content: "";
    position: absolute;
    inset: 1px;
    border-radius: 4px;
    border: 2px solid currentColor;
  }

  .random-slot-trigger-icon::before {
    transform: rotate(-10deg) translate(-2px, 1px);
  }

  .random-slot-trigger-icon::after {
    transform: rotate(12deg) translate(3px, -1px);
  }

  @media (max-width: 700px) {
    width: 100%;

    .random-slot-trigger {
      width: 100%;
    }
  }
`;

export const StyledRandomSlotPickerOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1450;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(14px, 4vw, 32px);
  background: rgba(2, 8, 12, 0.72);
  backdrop-filter: blur(5px);
  animation: ${overlayIn} 0.18s ease-out both;

  .random-slot-modal {
    width: min(520px, calc(100vw - 28px));
    max-height: calc(100dvh - 28px);
    border: 1px solid rgba(139, 177, 205, 0.18);
    border-radius: 18px;
    background: #102b35;
    box-shadow:
      0 28px 80px rgba(0, 0, 0, 0.46),
      inset 0 1px 0 rgba(255, 255, 255, 0.04);
    overflow: hidden;
    animation: ${modalIn} 0.24s cubic-bezier(0.2, 0.9, 0.2, 1.05) both;
  }

  .random-slot-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 22px 22px 18px;
    border-bottom: 1px solid rgba(139, 177, 205, 0.13);
    background: #0c222b;
  }

  .random-slot-header span {
    display: block;
    color: var(--crypto-green);
    font-size: 12px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: 0.04em;
  }

  .random-slot-header h2 {
    margin: 7px 0 0;
    color: #ffffff;
    font-family: var(--crypto-display-font);
    font-size: 28px;
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: 0;
  }

  .random-slot-header p {
    margin: 8px 0 0;
    color: #9dafbf;
    font-size: 13px;
    line-height: 1.35;
    font-weight: 600;
  }

  .random-slot-close {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    border: 0;
    border-radius: 11px;
    color: #d6e4eb;
    background: rgba(255, 255, 255, 0.07);
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
    transition:
      background 0.15s ease,
      transform 0.15s ease,
      color 0.15s ease;
  }

  .random-slot-close:hover,
  .random-slot-close:focus-visible {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.11);
    transform: translateY(-1px);
    outline: 0;
  }

  .random-slot-stage {
    display: flex;
    justify-content: center;
    padding: 24px 22px 18px;
    background:
      radial-gradient(circle at 50% 32%, rgba(89, 244, 119, 0.12), transparent 34%),
      #102b35;
  }

  .random-slot-card {
    position: relative;
    width: min(230px, 60vw);
    aspect-ratio: 5 / 7;
    margin: 0;
    border-radius: 14px;
    overflow: hidden;
    background:
      linear-gradient(150deg, rgba(89, 244, 119, 0.1), rgba(12, 32, 40, 0.94)),
      #071820;
    box-shadow:
      0 22px 44px rgba(0, 0, 0, 0.34),
      0 0 0 1px rgba(139, 177, 205, 0.16);
  }

  .random-slot-card.is-rolling {
    animation: ${rollPulse} 0.34s ease-in-out infinite;
  }

  .random-slot-card.is-rolling::before {
    content: "";
    position: absolute;
    z-index: 3;
    top: -15%;
    bottom: -15%;
    width: 46%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.18),
      transparent
    );
    animation: ${sweep} 0.72s ease-in-out infinite;
    pointer-events: none;
  }

  .random-slot-card img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .random-slot-card::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 48%, rgba(5, 8, 12, 0.9) 100%);
    pointer-events: none;
  }

  .random-slot-empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #d6e4eb;
    font-size: 16px;
    font-weight: 800;
  }

  .random-slot-card figcaption {
    position: absolute;
    z-index: 4;
    left: 12px;
    right: 12px;
    bottom: 14px;
    display: grid;
    gap: 5px;
    text-align: center;
  }

  .random-slot-card figcaption span {
    color: var(--crypto-green);
    font-size: 11px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: 0.03em;
  }

  .random-slot-card figcaption strong {
    color: #ffffff;
    font-family: var(--crypto-display-font);
    font-size: 18px;
    line-height: 1.14;
    font-weight: 700;
    text-shadow: 0 2px 9px rgba(0, 0, 0, 0.82);
    word-break: keep-all;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .random-slot-card figcaption small {
    color: #b8c8d4;
    font-size: 12px;
    line-height: 1.15;
    font-weight: 700;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.82);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .random-slot-error {
    margin: 0 22px 16px;
    border: 1px solid rgba(255, 133, 133, 0.24);
    border-radius: 10px;
    color: #ffabab;
    background: rgba(255, 70, 70, 0.06);
    padding: 12px 14px;
    font-size: 13px;
    font-weight: 700;
  }

  .random-slot-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 0 22px 22px;
  }

  .random-slot-actions button {
    height: 42px;
    min-width: 116px;
    border: 0;
    border-radius: 10px;
    padding: 0 16px;
    font-family: var(--crypto-display-font);
    font-size: 14px;
    line-height: 1;
    font-weight: 700;
    cursor: pointer;
    transition:
      filter 0.15s ease,
      transform 0.15s ease,
      background 0.15s ease,
      opacity 0.15s ease;
  }

  .random-slot-actions button:hover:not(:disabled),
  .random-slot-actions button:focus-visible:not(:disabled) {
    transform: translateY(-1px);
    outline: 0;
  }

  .random-slot-actions button:disabled {
    opacity: 0.52;
    cursor: default;
  }

  .random-slot-secondary {
    color: #d8e5ee;
    background: rgba(255, 255, 255, 0.08);
  }

  .random-slot-secondary:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }

  .random-slot-primary {
    color: #06140f;
    background: linear-gradient(135deg, #7cff91, #28d765 54%, #17ac4e);
    box-shadow: 0 12px 24px rgba(22, 184, 87, 0.22);
  }

  .random-slot-primary:hover:not(:disabled) {
    filter: brightness(1.04);
  }

  @media (max-width: 520px) {
    align-items: center;
    padding: 12px;

    .random-slot-modal {
      width: calc(100vw - 24px);
      border-radius: 16px;
    }

    .random-slot-header {
      padding: 18px 18px 15px;
    }

    .random-slot-header h2 {
      font-size: 24px;
    }

    .random-slot-stage {
      padding: 20px 18px 16px;
    }

    .random-slot-card {
      width: min(210px, 62vw);
    }

    .random-slot-actions {
      padding: 0 18px 18px;
    }

    .random-slot-actions button {
      flex: 1 1 0;
      min-width: 0;
      padding: 0 10px;
      font-size: 13px;
    }
  }
`;
